import { useState, useCallback, useRef, useMemo } from 'react'
import {
  Schedule,
  ScheduleRule,
  ScheduleOverride,
  DayOfWeek,
  OverrideType,
  AddReason,
  CancelReason,
} from '../types'
import { validateNoOverlap } from '../utils'

// ── Mocked Coordinator ──

const MOCK_SCHEDULES: Schedule[] = [
  {
    id: 'sched-001',
    clientSettingId: 'cs-001',
    name: 'Trial',
    cycleLengthWeeks: 2,
    cycleAnchorDate: '2026-01-05',
    effectiveFrom: '2026-01-01',
    effectiveUntil: '2026-06-30',
    defaultCutoffDaysBefore: 1,
    defaultCutoffTime: 1400,
    defaultDropoffTime: 1130,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'sched-002',
    clientSettingId: 'cs-001',
    name: 'Default',
    cycleLengthWeeks: 1,
    cycleAnchorDate: '2026-07-06',
    effectiveFrom: '2026-07-01',
    effectiveUntil: null,
    defaultCutoffDaysBefore: null, // inherits from global
    defaultCutoffTime: null,
    defaultDropoffTime: 1200,
    createdAt: '2026-06-01T00:00:00Z',
    updatedAt: '2026-06-01T00:00:00Z',
  },
]

const MOCK_RULES: Record<string, ScheduleRule[]> = {
  'sched-001': [
    // Week 1: Mon, Wed, Fri
    {
      id: 'rule-1-0',
      scheduleId: 'sched-001',
      cycleWeek: 1,
      dayOfWeek: DayOfWeek.Monday,
      cutoffDaysBefore: 2, // weekend gap
      cutoffTime: 1400,
      dropoffTime: null,
    },
    {
      id: 'rule-1-2',
      scheduleId: 'sched-001',
      cycleWeek: 1,
      dayOfWeek: DayOfWeek.Wednesday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
    },
    {
      id: 'rule-1-4',
      scheduleId: 'sched-001',
      cycleWeek: 1,
      dayOfWeek: DayOfWeek.Friday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: 1100, // early dropoff on Fridays
    },
    // Week 2: Tue, Thu
    {
      id: 'rule-2-1',
      scheduleId: 'sched-001',
      cycleWeek: 2,
      dayOfWeek: DayOfWeek.Tuesday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
    },
    {
      id: 'rule-2-3',
      scheduleId: 'sched-001',
      cycleWeek: 2,
      dayOfWeek: DayOfWeek.Thursday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
    },
  ],
  'sched-002': [
    // Simple M-F
    {
      id: 'rule-s2-0',
      scheduleId: 'sched-002',
      cycleWeek: 1,
      dayOfWeek: DayOfWeek.Monday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
    },
    {
      id: 'rule-s2-1',
      scheduleId: 'sched-002',
      cycleWeek: 1,
      dayOfWeek: DayOfWeek.Tuesday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
    },
    {
      id: 'rule-s2-2',
      scheduleId: 'sched-002',
      cycleWeek: 1,
      dayOfWeek: DayOfWeek.Wednesday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
    },
    {
      id: 'rule-s2-3',
      scheduleId: 'sched-002',
      cycleWeek: 1,
      dayOfWeek: DayOfWeek.Thursday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
    },
    {
      id: 'rule-s2-4',
      scheduleId: 'sched-002',
      cycleWeek: 1,
      dayOfWeek: DayOfWeek.Friday,
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
    },
  ],
}

const MOCK_OVERRIDES: Record<string, ScheduleOverride[]> = {
  'sched-001': [
    {
      id: 'ovr-001',
      scheduleId: 'sched-001',
      date: '2026-05-25',
      overrideType: OverrideType.Cancel,
      reason: CancelReason.OfficeClosure_Planned,
      notes: 'Memorial Day',
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
      createdBy: 'user-admin',
      createdAt: '2026-01-01T00:00:00Z',
      updatedAt: '2026-01-01T00:00:00Z',
    },
    {
      id: 'ovr-003',
      scheduleId: 'sched-001',
      date: '2026-03-21',
      overrideType: OverrideType.Add,
      reason: AddReason.ExecutiveOrVIPVisit,
      notes: 'Board member visiting — Saturday lunch',
      cutoffDaysBefore: 0,
      cutoffTime: 1000,
      dropoffTime: 1200,
      createdBy: 'user-admin',
      createdAt: '2026-01-01T00:00:00Z',
      updatedAt: '2026-01-01T00:00:00Z',
    },
    {
      id: 'ovr-004',
      scheduleId: 'sched-001',
      date: '2026-06-03',
      overrideType: OverrideType.Modify,
      reason: null,
      notes: 'Extended ordering window for product launch',
      cutoffDaysBefore: 0,
      cutoffTime: 900,
      dropoffTime: 1300, // later dropoff for event
      createdBy: 'user-admin',
      createdAt: '2026-01-01T00:00:00Z',
      updatedAt: '2026-01-01T00:00:00Z',
    },
  ],
  'sched-002': [
    {
      id: 'ovr-005',
      scheduleId: 'sched-002',
      date: '2026-11-26',
      overrideType: OverrideType.Cancel,
      reason: CancelReason.OfficeClosure_Planned,
      notes: 'Thanksgiving Day',
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
      createdBy: 'user-admin',
      createdAt: '2026-06-01T00:00:00Z',
      updatedAt: '2026-06-01T00:00:00Z',
    },
    {
      id: 'ovr-006',
      scheduleId: 'sched-002',
      date: '2026-11-27',
      overrideType: OverrideType.Cancel,
      reason: CancelReason.OfficeClosure_Planned,
      notes: 'Day after Thanksgiving',
      cutoffDaysBefore: null,
      cutoffTime: null,
      dropoffTime: null,
      createdBy: 'user-admin',
      createdAt: '2026-06-01T00:00:00Z',
      updatedAt: '2026-06-01T00:00:00Z',
    },
  ],
}

async function fetchScheduleData(_clientSettingId: string) {
  await new Promise((resolve) => setTimeout(resolve, 300))

  return {
    schedules: MOCK_SCHEDULES.map((s) => ({ ...s })),
    rulesBySchedule: Object.fromEntries(
      Object.entries(MOCK_RULES).map(([k, v]) => [k, v.map((r) => ({ ...r }))]),
    ) as Record<string, ScheduleRule[]>,
    overridesBySchedule: Object.fromEntries(
      Object.entries(MOCK_OVERRIDES).map(([k, v]) => [
        k,
        v.map((o) => ({ ...o })),
      ]),
    ) as Record<string, ScheduleOverride[]>,
  }
}

let _nextId = 100

function generateId(prefix: string): string {
  _nextId += 1

  return `${prefix}-${_nextId}`
}

// ── Hook ──

export interface UseScheduleStateReturn {
  schedules: Schedule[]
  selectedScheduleId: string | null
  rules: ScheduleRule[] // rules for the selected schedule
  overrides: ScheduleOverride[] // overrides for the selected schedule
  allRulesBySchedule: Map<string, ScheduleRule[]>
  allOverridesBySchedule: Map<string, ScheduleOverride[]>
  isLoading: boolean
  isDirty: boolean
  // Schedule selection
  selectSchedule: (id: string) => void
  // Schedule mutations
  createSchedule: () => string // returns new schedule id
  updateSchedule: (partial: Partial<Schedule>) => void
  deleteSchedule: (id: string) => void
  // Rule mutations
  addRule: (cycleWeek: number, dayOfWeek: DayOfWeek) => void
  updateRule: (id: string, partial: Partial<ScheduleRule>) => void
  removeRule: (id: string) => void
  // Override mutations
  addOverride: (override: {
    date: string
    overrideType: OverrideType
    reason: AddReason | CancelReason | null
    notes: string | null
    cutoffDaysBefore: number | null
    cutoffTime: number | null
    dropoffTime: number | null
  }) => void
  removeOverride: (id: string) => void
  // Persistence
  saveSchedule: () => Promise<void>
  discardChanges: () => void
  loadSchedule: (clientSettingId: string) => Promise<void>
  // Validation
  overlapError: string | null
}

export function useScheduleState(): UseScheduleStateReturn {
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(
    null,
  )
  const [rulesBySchedule, setRulesBySchedule] = useState<
    Record<string, ScheduleRule[]>
  >({})
  const [overridesBySchedule, setOverridesBySchedule] = useState<
    Record<string, ScheduleOverride[]>
  >({})
  const [isLoading, setIsLoading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const savedState = useRef<{
    schedules: Schedule[]
    rulesBySchedule: Record<string, ScheduleRule[]>
    overridesBySchedule: Record<string, ScheduleOverride[]>
  }>({ schedules: [], rulesBySchedule: {}, overridesBySchedule: {} })

  // Derived: rules and overrides for currently selected schedule
  const rules = selectedScheduleId
    ? rulesBySchedule[selectedScheduleId] || []
    : []
  const overrides = selectedScheduleId
    ? overridesBySchedule[selectedScheduleId] || []
    : []

  // Map versions for the resolver
  const allRulesBySchedule = useMemo(
    () => new Map(Object.entries(rulesBySchedule)),
    [rulesBySchedule],
  )
  const allOverridesBySchedule = useMemo(
    () => new Map(Object.entries(overridesBySchedule)),
    [overridesBySchedule],
  )

  // Overlap validation
  const overlapError = useMemo(() => validateNoOverlap(schedules), [schedules])

  const loadSchedule = useCallback(async (clientSettingId: string) => {
    setIsLoading(true)
    try {
      const data = await fetchScheduleData(clientSettingId)
      setSchedules(data.schedules)
      setRulesBySchedule(data.rulesBySchedule)
      setOverridesBySchedule(data.overridesBySchedule)
      savedState.current = {
        schedules: data.schedules,
        rulesBySchedule: data.rulesBySchedule,
        overridesBySchedule: data.overridesBySchedule,
      }
      // Select the first schedule by default
      if (data.schedules.length > 0) {
        setSelectedScheduleId(data.schedules[0].id)
      }
      setIsDirty(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const selectSchedule = useCallback((id: string) => {
    setSelectedScheduleId(id)
  }, [])

  const createSchedule = useCallback((): string => {
    const id = generateId('sched')
    const today = new Date().toISOString().slice(0, 10)
    const newSchedule: Schedule = {
      id,
      clientSettingId: schedules[0]?.clientSettingId || '',
      name: null,
      cycleLengthWeeks: 1,
      cycleAnchorDate: today,
      effectiveFrom: today,
      effectiveUntil: null,
      defaultCutoffDaysBefore: null,
      defaultCutoffTime: null,
      defaultDropoffTime: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setSchedules((prev) => [...prev, newSchedule])
    setRulesBySchedule((prev) => ({ ...prev, [id]: [] }))
    setOverridesBySchedule((prev) => ({ ...prev, [id]: [] }))
    setSelectedScheduleId(id)
    setIsDirty(true)

    return id
  }, [schedules])

  const deleteSchedule = useCallback((id: string) => {
    setSchedules((prev) => prev.filter((s) => s.id !== id))
    setRulesBySchedule((prev) => {
      const next = { ...prev }
      delete next[id]

      return next
    })
    setOverridesBySchedule((prev) => {
      const next = { ...prev }
      delete next[id]

      return next
    })
    setSelectedScheduleId((prev) => {
      if (prev === id) {
        return null
      }

      return prev
    })
    setIsDirty(true)
  }, [])

  const updateSchedule = useCallback(
    (partial: Partial<Schedule>) => {
      setSchedules((prev) =>
        prev.map((s) =>
          s.id === selectedScheduleId ? { ...s, ...partial } : s,
        ),
      )
      setIsDirty(true)
    },
    [selectedScheduleId],
  )

  const addRule = useCallback(
    (cycleWeek: number, dayOfWeek: DayOfWeek) => {
      if (!selectedScheduleId) {
        return
      }
      const newRule: ScheduleRule = {
        id: generateId('rule'),
        scheduleId: selectedScheduleId,
        cycleWeek,
        dayOfWeek,
        cutoffDaysBefore: null,
        cutoffTime: null,
        dropoffTime: null,
      }
      setRulesBySchedule((prev) => ({
        ...prev,
        [selectedScheduleId]: [...(prev[selectedScheduleId] || []), newRule],
      }))
      setIsDirty(true)
    },
    [selectedScheduleId],
  )

  const updateRule = useCallback(
    (id: string, partial: Partial<ScheduleRule>) => {
      if (!selectedScheduleId) {
        return
      }
      setRulesBySchedule((prev) => ({
        ...prev,
        [selectedScheduleId]: (prev[selectedScheduleId] || []).map((r) =>
          r.id === id ? { ...r, ...partial } : r,
        ),
      }))
      setIsDirty(true)
    },
    [selectedScheduleId],
  )

  const removeRule = useCallback(
    (id: string) => {
      if (!selectedScheduleId) {
        return
      }
      setRulesBySchedule((prev) => ({
        ...prev,
        [selectedScheduleId]: (prev[selectedScheduleId] || []).filter(
          (r) => r.id !== id,
        ),
      }))
      setIsDirty(true)
    },
    [selectedScheduleId],
  )

  const addOverride = useCallback(
    (input: {
      date: string
      overrideType: OverrideType
      reason: AddReason | CancelReason | null
      notes: string | null
      cutoffDaysBefore: number | null
      cutoffTime: number | null
      dropoffTime: number | null
    }) => {
      if (!selectedScheduleId) {
        return
      }
      const newOverride: ScheduleOverride = {
        id: generateId('ovr'),
        scheduleId: selectedScheduleId,
        ...input,
        createdBy: 'current-user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      setOverridesBySchedule((prev) => ({
        ...prev,
        [selectedScheduleId]: [
          ...(prev[selectedScheduleId] || []),
          newOverride,
        ],
      }))
      setIsDirty(true)
    },
    [selectedScheduleId],
  )

  const removeOverride = useCallback(
    (id: string) => {
      if (!selectedScheduleId) {
        return
      }
      setOverridesBySchedule((prev) => ({
        ...prev,
        [selectedScheduleId]: (prev[selectedScheduleId] || []).filter(
          (o) => o.id !== id,
        ),
      }))
      setIsDirty(true)
    },
    [selectedScheduleId],
  )

  const saveSchedule = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 200))
    savedState.current = { schedules, rulesBySchedule, overridesBySchedule }
    setIsDirty(false)
  }, [schedules, rulesBySchedule, overridesBySchedule])

  const discardChanges = useCallback(() => {
    setSchedules(savedState.current.schedules)
    setRulesBySchedule(savedState.current.rulesBySchedule)
    setOverridesBySchedule(savedState.current.overridesBySchedule)
    if (savedState.current.schedules.length > 0) {
      setSelectedScheduleId(savedState.current.schedules[0].id)
    }
    setIsDirty(false)
  }, [])

  return {
    schedules,
    selectedScheduleId,
    rules,
    overrides,
    allRulesBySchedule,
    allOverridesBySchedule,
    isLoading,
    isDirty,
    selectSchedule,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    addRule,
    updateRule,
    removeRule,
    addOverride,
    removeOverride,
    saveSchedule,
    discardChanges,
    loadSchedule,
    overlapError,
  }
}
